<template>
  <div class="promotions-container">
    <div class="page-header">
      <h2>优惠营销管理</h2>
      <button class="btn btn-back" @click="$router.push('/seller/dashboard')">返回后台</button>
    </div>

    <!-- 创建新规则 -->
    <div class="card">
      <h3>创建优惠规则</h3>
      <div class="form-row">
        <div class="form-group">
          <label>活动名称</label>
          <input v-model="form.name" placeholder="如：618全场大促" class="form-input" />
        </div>
        <div class="form-group">
          <label>优惠类型</label>
          <select v-model="form.type" class="form-input">
            <option value="PERCENTAGE">百分比折扣</option>
            <option value="FIXED_AMOUNT">固定金额立减</option>
            <option value="FIXED_PRICE">固定到手价</option>
            <option value="THRESHOLD">满减</option>
          </select>
        </div>
        <div class="form-group">
          <label>优惠值</label>
          <input v-model.number="form.discountValue" type="number" step="0.01" class="form-input"
            :placeholder="discountValuePlaceholder" />
        </div>
        <div class="form-group" v-if="form.type === 'THRESHOLD'">
          <label>满减门槛(元)</label>
          <input v-model.number="form.thresholdAmount" type="number" step="0.01" class="form-input" />
        </div>
        <div class="form-group" v-if="form.type === 'PERCENTAGE'">
          <label>最大优惠(元)</label>
          <input v-model.number="form.maxDiscount" type="number" step="0.01" class="form-input" placeholder="上限，可不填" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>开始时间</label>
          <input v-model="form.startTime" type="datetime-local" class="form-input" />
        </div>
        <div class="form-group">
          <label>结束时间</label>
          <input v-model="form.endTime" type="datetime-local" class="form-input" />
        </div>
        <div class="form-group">
          <label>优先级</label>
          <input v-model.number="form.priority" type="number" class="form-input" placeholder="越大越优先" />
        </div>
        <div class="form-group">
          <label>总使用次数限制</label>
          <input v-model.number="form.usageLimit" type="number" class="form-input" placeholder="留空=不限" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>活动描述</label>
          <input v-model="form.description" class="form-input" placeholder="可选" style="width: 400px;" />
        </div>
        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" v-model="form.stackable" /> 可叠加
          </label>
        </div>
        <div class="form-group">
          <button class="btn btn-primary" @click="createRule">创建规则</button>
        </div>
      </div>
    </div>

    <!-- 规则列表 -->
    <div class="card">
      <h3>现有规则 ({{ rules.length }})</h3>
      <table class="rule-table" v-if="rules.length > 0">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>类型</th>
            <th>优惠值</th>
            <th>时间范围</th>
            <th>状态</th>
            <th>已用/上限</th>
            <th>优先级</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="rule in rules" :key="rule.id">
            <td>{{ rule.id }}</td>
            <td>{{ rule.name }}</td>
            <td>{{ typeLabel(rule.type) }}</td>
            <td>
              <span v-if="rule.type === 'PERCENTAGE'">{{ rule.discountValue }}%</span>
              <span v-else-if="rule.type === 'THRESHOLD'">满{{ rule.thresholdAmount }}减{{ rule.discountValue }}</span>
              <span v-else>¥{{ rule.discountValue }}</span>
            </td>
            <td class="time-cell">{{ formatDate(rule.startTime) }} ~ {{ formatDate(rule.endTime) }}</td>
            <td>
              <span :class="statusClass(rule.status)">{{ statusLabel(rule.status) }}</span>
            </td>
            <td>{{ rule.usedCount }}/{{ rule.usageLimit || '∞' }}</td>
            <td>{{ rule.priority }}</td>
            <td class="action-cell">
              <button class="btn btn-sm btn-secondary" @click="editRule(rule)">编辑</button>
              <button class="btn btn-sm" :class="rule.status === 1 ? 'btn-warning' : 'btn-success'"
                @click="toggleStatus(rule)">
                {{ rule.status === 1 ? '暂停' : '启用' }}
              </button>
              <button class="btn btn-sm btn-info" @click="manageScopes(rule)">范围</button>
              <button class="btn btn-sm btn-danger" @click="deleteRule(rule.id)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="empty-text">暂无优惠规则</p>
    </div>

    <!-- 编辑规则弹窗 -->
    <div class="modal-overlay" v-if="editingRule" @click.self="editingRule = null">
      <div class="modal-card">
        <h3>编辑规则</h3>
        <div class="form-group">
          <label>名称</label>
          <input v-model="editingRule.name" class="form-input" />
        </div>
        <div class="form-group">
          <label>描述</label>
          <input v-model="editingRule.description" class="form-input" />
        </div>
        <div class="form-group">
          <label>优惠值</label>
          <input v-model.number="editingRule.discountValue" type="number" step="0.01" class="form-input" />
        </div>
        <div class="form-group" v-if="editingRule.type === 'THRESHOLD'">
          <label>门槛金额</label>
          <input v-model.number="editingRule.thresholdAmount" type="number" step="0.01" class="form-input" />
        </div>
        <div class="form-group" v-if="editingRule.type === 'PERCENTAGE'">
          <label>最大优惠</label>
          <input v-model.number="editingRule.maxDiscount" type="number" step="0.01" class="form-input" />
        </div>
        <div class="form-group">
          <label>优先级</label>
          <input v-model.number="editingRule.priority" type="number" class="form-input" />
        </div>
        <div class="form-group">
          <label>使用次数上限</label>
          <input v-model.number="editingRule.usageLimit" type="number" class="form-input" />
        </div>
        <div class="form-group">
          <label>开始时间</label>
          <input v-model="editingRule.startTime" type="datetime-local" class="form-input" />
        </div>
        <div class="form-group">
          <label>结束时间</label>
          <input v-model="editingRule.endTime" type="datetime-local" class="form-input" />
        </div>
        <div class="checkbox-group">
          <label><input type="checkbox" v-model="editingRule.stackable" /> 可叠加</label>
        </div>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="updateRule">保存</button>
          <button class="btn btn-secondary" @click="editingRule = null">取消</button>
        </div>
      </div>
    </div>

    <!-- 适用范围弹窗 -->
    <div class="modal-overlay" v-if="scopingRule" @click.self="scopingRule = null">
      <div class="modal-card" style="width: 500px;">
        <h3>适用范围 - {{ scopingRule.name }}</h3>
        <div class="form-group">
          <label>范围类型</label>
          <select v-model="scopeForm.scopeType" class="form-input">
            <option value="ALL">全部商品</option>
            <option value="PRODUCT">指定商品</option>
            <option value="CATEGORY">指定分类</option>
            <option value="SUBCATEGORY">指定二级分类</option>
          </select>
        </div>
        <div class="form-group" v-if="scopeForm.scopeType === 'PRODUCT'">
          <label>选择商品</label>
          <select v-model="scopeForm.targetId" class="form-input">
            <option :value="null">请选择</option>
            <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }} (¥{{ p.price }})</option>
          </select>
        </div>
        <div class="form-group" v-if="scopeForm.scopeType === 'CATEGORY'">
          <label>选择分类</label>
          <select v-model="scopeForm.targetId" class="form-input">
            <option :value="null">请选择</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="form-group" v-if="scopeForm.scopeType === 'SUBCATEGORY'">
          <label>选择二级分类</label>
          <select v-model="scopeForm.targetId" class="form-input">
            <option :value="null">请选择</option>
            <option v-for="s in subCategories" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="form-group">
          <button class="btn btn-sm btn-primary" @click="addScope">添加范围</button>
        </div>
        <hr />
        <h4>当前适用范围</h4>
        <table class="scope-table">
          <thead><tr><th>类型</th><th>目标</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="scope in scopes" :key="scope.id">
              <td>{{ scopeTypeLabel(scope.scopeType) }}</td>
              <td>{{ scopeTargetName(scope) }}</td>
              <td><button class="btn btn-sm btn-danger" @click="removeScope(scope.id)">删除</button></td>
            </tr>
            <tr v-if="scopes.length === 0"><td colspan="3">暂无适用范围</td></tr>
          </tbody>
        </table>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="scopingRule = null">关闭</button>
        </div>
      </div>
    </div>

    <!-- 价格预览 -->
    <div class="card">
      <h3>价格预览</h3>
      <div class="form-row">
        <div class="form-group">
          <label>商品ID</label>
          <input v-model.number="previewProductId" type="number" class="form-input" placeholder="输入商品ID" />
        </div>
        <div class="form-group">
          <label>数量</label>
          <input v-model.number="previewQuantity" type="number" class="form-input" min="1" />
        </div>
        <div class="form-group">
          <button class="btn btn-primary" @click="previewPrice">查询价格</button>
        </div>
      </div>
      <div class="preview-result" v-if="pricePreview">
        <p>原价: <strong>¥{{ pricePreview.originalPrice.toFixed(2) }}</strong></p>
        <p>优惠: <strong style="color:#E02E24;">-¥{{ pricePreview.discountAmount.toFixed(2) }}</strong></p>
        <p>到手价: <strong style="color:green;">¥{{ pricePreview.finalPrice.toFixed(2) }}</strong></p>
        <p v-if="pricePreview.appliedRules && pricePreview.appliedRules.length > 0">
          适用规则: {{ pricePreview.appliedRules.join(', ') }}
        </p>
      </div>
    </div>

    <div v-if="error" class="error-msg">{{ error }}</div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SellerPromotions',
  data() {
    return {
      rules: [],
      products: [],
      categories: [],
      subCategories: [],
      scopes: [],
      form: {
        name: '',
        description: '',
        type: 'PERCENTAGE',
        discountValue: null,
        thresholdAmount: null,
        maxDiscount: null,
        startTime: '',
        endTime: '',
        priority: 0,
        stackable: false,
        usageLimit: null
      },
      editingRule: null,
      scopingRule: null,
      scopeForm: { scopeType: 'ALL', targetId: null },
      previewProductId: null,
      previewQuantity: 1,
      pricePreview: null,
      error: ''
    }
  },
  computed: {
    discountValuePlaceholder() {
      const map = {
        PERCENTAGE: '如20表示8折',
        FIXED_AMOUNT: '如10表示减10元',
        FIXED_PRICE: '如99表示到手99元',
        THRESHOLD: '如20表示减20元'
      }
      return map[this.form.type] || ''
    }
  },
  created() {
    this.fetchRules()
    this.fetchProducts()
    this.fetchCategories()
    this.fetchSubCategories()
  },
  methods: {
    // 获取商家ID（从本地存储获取）
    getSellerId() {
      const seller = JSON.parse(localStorage.getItem('sellerInfo') || '{}')
      return seller.id || 1
    },

    async fetchRules() {
      try {
        const res = await axios.get('/promotions/seller/' + this.getSellerId())
        this.rules = res.data?.data || []
      } catch (e) {
        this.error = '获取规则失败'
      }
    },

    async fetchProducts() {
      try {
        const res = await axios.get('/products')
        this.products = Array.isArray(res.data) ? res.data : (res.data?.data || [])
      } catch (e) { /* ignore */ }
    },

    async fetchCategories() {
      try {
        const res = await axios.get('/categories/active')
        this.categories = res.data?.data || []
      } catch (e) { /* ignore */ }
    },

    async fetchSubCategories() {
      try {
        const res = await axios.get('/sub-categories/active')
        this.subCategories = Array.isArray(res.data) ? res.data : (res.data?.data || [])
      } catch (e) { /* ignore */ }
    },

    async createRule() {
      if (!this.form.name || !this.form.type || this.form.discountValue === null) {
        alert('请填写必要字段')
        return
      }
      try {
        const body = {
          ...this.form,
          sellerId: this.getSellerId(),
          status: 1,
          startTime: this.form.startTime ? this.form.startTime.replace('T', 'T') + ':00' : '2026-01-01T00:00:00',
          endTime: this.form.endTime ? this.form.endTime.replace('T', 'T') + ':00' : '2027-12-31T23:59:59'
        }
        await axios.post('/promotions', body)
        this.form = { name: '', description: '', type: 'PERCENTAGE', discountValue: null, thresholdAmount: null,
          maxDiscount: null, startTime: '', endTime: '', priority: 0, stackable: false, usageLimit: null }
        this.fetchRules()
      } catch (e) {
        alert('创建失败: ' + (e.response?.data?.message || e.message))
      }
    },

    editRule(rule) {
      this.editingRule = JSON.parse(JSON.stringify(rule))
      if (this.editingRule.startTime) {
        this.editingRule.startTime = this.editingRule.startTime.substring(0, 16)
      }
      if (this.editingRule.endTime) {
        this.editingRule.endTime = this.editingRule.endTime.substring(0, 16)
      }
    },

    async updateRule() {
      try {
        const r = this.editingRule
        const body = {
          name: r.name, description: r.description, type: r.type,
          discountValue: r.discountValue, thresholdAmount: r.thresholdAmount,
          maxDiscount: r.maxDiscount, priority: r.priority, stackable: r.stackable,
          usageLimit: r.usageLimit, status: r.status,
          startTime: r.startTime ? r.startTime + ':00' : '',
          endTime: r.endTime ? r.endTime + ':00' : '',
          sellerId: r.sellerId
        }
        await axios.put('/promotions/' + r.id, body)
        this.editingRule = null
        this.fetchRules()
      } catch (e) {
        alert('更新失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async deleteRule(id) {
      if (!confirm('确定删除此规则？')) return
      try {
        await axios.delete('/promotions/' + id)
        this.fetchRules()
      } catch (e) {
        alert('删除失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async toggleStatus(rule) {
      const newStatus = rule.status === 1 ? 2 : 1
      try {
        await axios.patch('/promotions/' + rule.id + '/status?status=' + newStatus)
        this.fetchRules()
      } catch (e) {
        alert('操作失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async manageScopes(rule) {
      this.scopingRule = rule
      this.scopeForm = { scopeType: 'ALL', targetId: null }
      try {
        const res = await axios.get('/promotions/' + rule.id)
        this.scopes = res.data?.scopes || []
      } catch (e) {
        this.scopes = []
      }
    },

    async addScope() {
      if (this.scopeForm.scopeType !== 'ALL' && !this.scopeForm.targetId) {
        alert('请选择目标')
        return
      }
      this.scopes.push({
        rule: { id: this.scopingRule.id },
        scopeType: this.scopeForm.scopeType,
        targetId: this.scopeForm.scopeType === 'ALL' ? null : this.scopeForm.targetId
      })
      try {
        await axios.put('/promotions/' + this.scopingRule.id + '/scopes', this.scopes)
        this.scopeForm = { scopeType: 'ALL', targetId: null }
      } catch (e) {
        alert('设置失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async removeScope(index) {
      this.scopes.splice(index, 1)
      try {
        await axios.put('/promotions/' + this.scopingRule.id + '/scopes', this.scopes)
      } catch (e) {
        alert('删除失败: ' + (e.response?.data?.message || e.message))
      }
    },

    scopeTypeLabel(type) {
      const map = { ALL: '全部', PRODUCT: '指定商品', CATEGORY: '指定分类', SUBCATEGORY: '指定二级分类' }
      return map[type] || type
    },

    scopeTargetName(scope) {
      if (scope.scopeType === 'ALL') return '全部商品'
      if (scope.scopeType === 'PRODUCT') {
        const p = this.products.find(x => x.id === scope.targetId)
        return p ? p.name : 'ID:' + scope.targetId
      }
      if (scope.scopeType === 'CATEGORY') {
        const c = this.categories.find(x => x.id === scope.targetId)
        return c ? c.name : 'ID:' + scope.targetId
      }
      if (scope.scopeType === 'SUBCATEGORY') {
        const s = this.subCategories.find(x => x.id === scope.targetId)
        return s ? s.name : 'ID:' + scope.targetId
      }
      return '-'
    },

    async previewPrice() {
      if (!this.previewProductId) return
      try {
        const res = await axios.get('/promotions/product/' + this.previewProductId +
          '/price?quantity=' + this.previewQuantity)
        this.pricePreview = res.data?.data
      } catch (e) {
        alert('查询失败')
      }
    },

    typeLabel(type) {
      const map = { PERCENTAGE: '百分比折扣', FIXED_AMOUNT: '固定金额', FIXED_PRICE: '固定到手价', THRESHOLD: '满减' }
      return map[type] || type
    },

    statusLabel(s) {
      const map = { 0: '草稿', 1: '启用', 2: '暂停', 3: '结束' }
      return map[s] || '未知'
    },

    statusClass(s) {
      return s === 1 ? 'status-active' : 'status-inactive'
    },

    formatDate(d) {
      if (!d) return '-'
      return d.substring(0, 16).replace('T', ' ')
    }
  }
}
</script>

<style scoped>
.promotions-container { padding: 20px; max-width: 1200px; margin: 0 auto; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-header h2 { color: #E02E24; margin: 0; }
.card { background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); padding: 20px; margin-bottom: 20px; }
.card h3 { margin-top: 0; color: #333; border-bottom: 2px solid #E02E24; padding-bottom: 8px; }
.form-row { display: flex; gap: 15px; flex-wrap: wrap; align-items: flex-end; margin-bottom: 15px; }
.form-group { display: flex; flex-direction: column; }
.form-group label { font-size: 13px; color: #666; margin-bottom: 4px; }
.form-input { padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; min-width: 120px; }
.form-input:focus { outline: none; border-color: #E02E24; }
.checkbox-group { display: flex; align-items: center; padding-top: 20px; }
.checkbox-group label { display: flex; align-items: center; gap: 6px; cursor: pointer; }
.btn { padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; }
.btn-primary { background: #E02E24; color: #fff; }
.btn-primary:hover { background: #c02820; }
.btn-secondary { background: #6c757d; color: #fff; }
.btn-danger { background: #dc3545; color: #fff; }
.btn-warning { background: #ffc107; color: #333; }
.btn-success { background: #28a745; color: #fff; }
.btn-info { background: #17a2b8; color: #fff; }
.btn-back { background: #6c757d; color: #fff; }
.btn-sm { padding: 4px 10px; font-size: 12px; }
.rule-table { width: 100%; border-collapse: collapse; }
.rule-table th, .rule-table td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #eee; font-size: 13px; }
.rule-table th { background: #f8f9fa; color: #555; font-weight: 600; }
.time-cell { font-size: 11px; color: #888; }
.action-cell { display: flex; gap: 4px; flex-wrap: wrap; }
.status-active { color: #28a745; font-weight: bold; }
.status-inactive { color: #999; }
.empty-text { color: #999; text-align: center; padding: 30px; }
.error-msg { color: #dc3545; background: #f8d7da; padding: 10px; border-radius: 4px; margin-top: 10px; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5);
  display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 25px; width: 450px; max-height: 80vh; overflow-y: auto; }
.modal-card h3 { margin-top: 0; color: #E02E24; }
.modal-card .form-group { margin-bottom: 10px; }
.modal-card .form-input { width: 100%; box-sizing: border-box; }
.modal-actions { display: flex; gap: 10px; margin-top: 15px; justify-content: flex-end; }
.scope-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
.scope-table th, .scope-table td { padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 13px; }
.preview-result { background: #f8f9fa; padding: 15px; border-radius: 4px; margin-top: 10px; }
.preview-result p { margin: 6px 0; }
</style>
